import { GetFacilityPatientsQuery, UpdateFacilityPatientsMutation } from '@/API';
import { updateFacilityPatients } from '@/graphql/mutations';
import { getFacilityPatients } from '@/graphql/queries';
import { amplifyClient } from '@/lib/amplifyClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect } from 'react';
import { executeQuery } from '@/client/graphql';

export const RESERVATION_STEP = {
  Menu: '診療メニュー一覧',
  MenuConfirm: '診療メニュー確認',
  Date: '日付選択',
  ReserveConfirm: '予約確認',
  Complete: '予約完了'
} as const;
type ReservationStep = (typeof RESERVATION_STEP)[keyof typeof RESERVATION_STEP];

const REGISTED_STATUS = {
  ReVisit: 'Revisit',
  FirstVisit: 'First'
} as const;
type RegistedStatus = (typeof REGISTED_STATUS)[keyof typeof REGISTED_STATUS];

/**
 * 初診・再診の判定
 */
const getRegistedStatus = async (facilityId: string, pageTitle: ReservationStep): Promise<RegistedStatus> => {
  const session = await fetchAuthSession();
  if (!session.userSub) return REGISTED_STATUS.FirstVisit;

  const client = amplifyClient();
  const result = await executeQuery<GetFacilityPatientsQuery>(
    getFacilityPatients,
    { patientId: session.userSub, facilityId },
    'userPool'
  );

  const patientData = result.getFacilityPatients;
  // NOTE: 完了画面は患者データの作成日時から初診・最新を判定
  if (pageTitle !== RESERVATION_STEP.Complete) {
    return patientData ? REGISTED_STATUS.ReVisit : REGISTED_STATUS.FirstVisit;
  } else {
    if (!patientData) return REGISTED_STATUS.FirstVisit;
    if (!patientData.isFirstVisit) return REGISTED_STATUS.ReVisit;

    // 初診フラグが立っていた場合は、フラグをfalseにして初診として判定
    await executeQuery<UpdateFacilityPatientsMutation>(
      updateFacilityPatients,
      {
        input: {
          patientId: session.userSub,
          facilityId: facilityId,
          isFirstVisit: false
        }
      },
      'userPool'
    );
    return REGISTED_STATUS.FirstVisit;
  }
};

export const usePageViewEvent = ({ pageTitle, facilityId }: { pageTitle: ReservationStep; facilityId?: number }) => {
  useEffect(() => {
    if (!facilityId) {
      return;
    }
    getRegistedStatus(facilityId.toString(), pageTitle).then((registedStatus) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pageChange',
        pageTitle,
        registedStatus
      });
    });
  }, [pageTitle, facilityId]);
};

export const dialogOpenEvent = ({ dialogTitle, facilityId }: { dialogTitle: ReservationStep; facilityId?: number }) => {
  if (!facilityId) {
    return;
  }
  getRegistedStatus(facilityId.toString(), dialogTitle).then((registedStatus) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'dialogOpen',
      dialogTitle,
      registedStatus
    });
  });
};
